import { Eye, Flag, Trash } from "phosphor-react";
import useLocalStorage from "../../../hooks/useLocalStorage";
import {
    type ApiFileTransferItem,
    type ApiGetFileTransfersResponse,
} from "../../../models/api/admin/fileSafe";
import PaginationNav from "../../PaginationNav";
import Table, { type TableHeader } from "../../ReusableComponents/Table";
import {
    initFileSafeSentTableContents,
    initFileSafeSentTableHeaders,
} from "./FileSafeSentTableInit";
import { appPaths } from "../../../Routes";
import { useNavigate } from "react-router-dom";
import ConfirmationDialog from "../../ReusableComponents/ConfirmationDialog";
import useFileSafeTableActions from "../useFileSafeTableActions";
import useAuth from "../../../auth";
import { isS2dUser } from "../../../auth/userAccessHelpers";

type FileSafeSentTableProps = {
    tableData: ApiGetFileTransfersResponse;
    refetchTableData: () => void;
    pageNumber: number;
    setPageNumber: (value: number) => void;
    pageSize: number;
    setPageSize: (value: number) => void;
};

const FileSafeSentTable: React.FC<FileSafeSentTableProps> = ({
    tableData,
    refetchTableData,
    pageNumber,
    setPageNumber,
    pageSize,
    setPageSize,
}) => {
    const [tableHeaders, setTableHeaders] = useLocalStorage<TableHeader[]>(
        "FileSafeSentTableHeaders",
        initFileSafeSentTableHeaders
    );

    const {
        handleSelectedTransfer,
        deleteDialogOpen,
        setDeleteDialogOpen,
        flagForDeletionDialogOpen,
        setFlagForDeletionDialogOpen,
        handleDeleteTransfer,
        handleFlagForDeletion,
        isDeletingTransfer,
        isMarkingForDeletion,
        allowMarkForDeletion,
        markForDeletionMessage,
    } = useFileSafeTableActions(tableData, refetchTableData);

    const navigate = useNavigate();
    const { decodedToken } = useAuth();

    const transferTableMenuItems = (data: ApiFileTransferItem) => {
        const id = data.transferId;
        const menuItems = [
            {
                key: "view",
                onClick: () => navigate(appPaths.adminViewTransfer(id)),
                icon: <Eye size={20} weight="fill" />,
                text: "View transfer",
            },
            {
                key: "flag",
                onClick: () => {
                    handleSelectedTransfer(id, "flag");
                },
                icon: <Flag size={20} weight="fill" />,
                text: "Flag for deletion",
            },
        ];

        if (isS2dUser(decodedToken?.role)) {
            menuItems.push({
                key: "delete",
                onClick: () => {
                    handleSelectedTransfer(id, "delete");
                },
                icon: <Trash size={20} weight="fill" />,
                text: "Delete transfer",
            });
        }

        return menuItems;
    };

    return (
        <>
            <Table
                tableHeaders={tableHeaders}
                setTableHeaders={setTableHeaders}
                tableContents={initFileSafeSentTableContents(tableData?.items)}
                menuItems={transferTableMenuItems}
            />
            <PaginationNav
                setPageNumber={setPageNumber}
                pageSize={pageSize}
                setPageSize={setPageSize}
                totalCount={tableData.totalCount}
                totalPages={tableData.totalPages}
                currentPage={pageNumber}
                hasPreviousPage={tableData.hasPreviousPage}
                hasNextPage={tableData.hasNextPage}
            />
            <ConfirmationDialog
                open={deleteDialogOpen}
                title="Delete transfer?"
                caption="This transfer will be permanently deleted."
                onConfirm={handleDeleteTransfer}
                isLoading={isDeletingTransfer}
                onClose={() => setDeleteDialogOpen(false)}
                variant="danger"
            />
            <ConfirmationDialog
                open={flagForDeletionDialogOpen}
                title="Flag transfer for deletion?"
                caption={markForDeletionMessage()}
                onConfirm={handleFlagForDeletion}
                isLoading={isMarkingForDeletion}
                onClose={() => setFlagForDeletionDialogOpen(false)}
                variant="danger"
                disabled={!allowMarkForDeletion}
            />
        </>
    );
};

export default FileSafeSentTable;
